import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { getOrganizations } from '../../../../actions/organizations'
import { ChevronDownIcon, UserIcon, XIcon } from '../../../../assets/svg'


const STATUS_OPTIONS = [
  { value: 'planned',     label: 'Планируется' },
  { value: 'accepting',   label: 'Приём заявок' },
  { value: 'in_progress', label: 'Проводится' },
  { value: 'finished',    label: 'Завершено' }
]

const EMPTY_VALUES = {
  name: '',
  description: '',
  location: '',
  organizer_org_id: '',
  start_date: '',
  end_date: '',
  registration_start: '',
  registration_end: '',
  status: 'planned',
  commission: []
}

const toFormValues = (data) => {
  if (!data) return EMPTY_VALUES
  return {
    name: data.name || '',
    description: data.description || '',
    location: data.location || '',
    organizer_org_id: data.organizer_org_id ? String(data.organizer_org_id) : '',
    start_date: data.start_date || '',
    end_date: data.end_date || '',
    registration_start: data.registration_start || '',
    registration_end: data.registration_end || '',
    status: data.status || 'planned',
    commission: (data.commission || []).map((c) => ({ name_snapshot: c.name_snapshot }))
  }
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Введите название мероприятия'),
  organizer_org_id: Yup.string().required('Выберите ответственного'),
  start_date: Yup.string().required('Укажите дату начала'),
  end_date: Yup.string()
    .required('Укажите дату окончания')
    .test('after-start', 'Не может быть раньше даты начала', function (value) {
      const { start_date } = this.parent
      if (!value || !start_date) return true
      return value >= start_date
    })
})

const Field = ({ label, hint, error, children }) => (
  <label className="flex flex-col gap-[0.375rem]">
    <span className="text-[0.812rem] text-[#6b7280]">{label}</span>
    {children}
    {error
      ? <span className="text-[0.812rem] text-[#ef4444]">{error}</span>
      : hint && <span className="text-[0.812rem] italic text-[#9ca3af]">{hint}</span>}
  </label>
)

const AddEditModal = ({ open, mode = 'edit', initialData, onClose, onSave }) => {
  const dispatch = useDispatch()
  const organizations = useSelector((state) => state.organizations.list) || []

  const [commissionInput, setCommissionInput] = useState('')

  const initialValues = useMemo(() => toFormValues(initialData), [initialData])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      onSave?.({
        name: values.name.trim(),
        description: values.description.trim() || null,
        location: values.location.trim() || null,
        organizer_org_id: Number(values.organizer_org_id),
        start_date: values.start_date,
        end_date: values.end_date,
        registration_start: values.registration_start || null,
        registration_end: values.registration_end || null,
        status: values.status,
        commission: values.commission.map((c) => c.name_snapshot)
      })
    }
  })

  useEffect(() => {
    if (!open) return
    setCommissionInput('')
    formik.resetForm({ values: initialValues })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues])

  useEffect(() => {
    if (open && !organizations.length) dispatch(getOrganizations())
  }, [open, organizations.length, dispatch])

  if (!open) return null

  const title = mode === 'add' ? 'Добавление мероприятия' : 'Редактирование мероприятия'
  const submitLabel = mode === 'add' ? 'Создать' : 'Сохранить изменения'

  const errorOf = (field) => (formik.touched[field] && formik.errors[field]) || undefined

  const addCommissionMember = () => {
    const name = commissionInput.trim()
    if (!name) return
    formik.setFieldValue('commission', [...formik.values.commission, { name_snapshot: name }])
    setCommissionInput('')
  }

  const removeCommissionMember = (idx) => {
    formik.setFieldValue(
      'commission',
      formik.values.commission.filter((_, i) => i !== idx)
    )
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(17,24,39,0.45)] p-[0.75rem] sm:p-[1rem]"
      onMouseDown={onClose}
    >
      <form
        onSubmit={formik.handleSubmit}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex max-h-[95vh] w-full max-w-[56rem] flex-col overflow-hidden rounded-[16px] sm:rounded-[20px] bg-white shadow-[0px_20px_60px_rgba(0,0,0,0.15)]"
      >
        <div className="flex items-start justify-between px-[1.25rem] sm:px-[2rem] pt-[1.25rem] sm:pt-[2rem]">
          <h2 className="m-0 text-[1.125rem] sm:text-[1.5rem] font-semibold text-[#111827]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-[2rem] w-[2rem] items-center justify-center rounded-[8px] hover:bg-[#f3f4f6] shrink-0"
          >
            <XIcon />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-[1rem] sm:gap-[1.25rem] overflow-y-auto px-[1.25rem] sm:px-[2rem] py-[1rem] sm:py-[1.5rem]">
          <Field label="Название мероприятия" error={errorOf('name')}>
            <input
              type="text"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Название мероприятия"
              className="h-[2.75rem] w-full rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] text-[0.938rem] text-[#111827] placeholder:text-[#9ca3af] outline-none focus:border-[#0d9488]"
            />
          </Field>

          <Field
            label="Описание мероприятия"
            hint="Описание будет видно участникам мероприятия. Пожалуйста, опишите событие понятно и подробно."
          >
            <textarea
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Описание мероприятия"
              rows={4}
              className="w-full resize-none rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] py-[0.75rem] text-[0.938rem] text-[#111827] placeholder:text-[#9ca3af] outline-none focus:border-[#0d9488]"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1rem] sm:gap-[1.25rem]">
            <Field label="Место проведения">
              <input
                type="text"
                name="location"
                value={formik.values.location}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Место проведения"
                className="h-[2.75rem] w-full rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] text-[0.938rem] text-[#111827] placeholder:text-[#9ca3af] outline-none focus:border-[#0d9488]"
              />
            </Field>

            <Field label="Ответственный" error={errorOf('organizer_org_id')}>
              <div className="relative">
                <span className="pointer-events-none absolute left-[0.875rem] top-1/2 -translate-y-1/2">
                  <UserIcon />
                </span>
                <select
                  name="organizer_org_id"
                  value={formik.values.organizer_org_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="h-[2.75rem] w-full appearance-none rounded-[10px] border border-solid border-[#e5e7eb] bg-white pl-[2.5rem] pr-[2.5rem] text-[0.938rem] text-[#111827] outline-none focus:border-[#0d9488]"
                >
                  <option value="">Выберите училище</option>
                  {organizations.map((org) => (
                    <option key={org.idOrganization} value={org.idOrganization}>
                      {org.shortName || org.OrgName}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-[0.875rem] top-1/2 -translate-y-1/2">
                  <ChevronDownIcon />
                </span>
              </div>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1rem] sm:gap-[1.25rem]">
            <Field label="Дата начала" error={errorOf('start_date')}>
              <input
                type="date"
                name="start_date"
                value={formik.values.start_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="h-[2.75rem] w-full rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] text-[0.938rem] text-[#111827] outline-none focus:border-[#0d9488]"
              />
            </Field>
            <Field label="Дата окончания" error={errorOf('end_date')}>
              <input
                type="date"
                name="end_date"
                value={formik.values.end_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="h-[2.75rem] w-full rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] text-[0.938rem] text-[#111827] outline-none focus:border-[#0d9488]"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1rem] sm:gap-[1.25rem]">
            <Field label="Приём заявок с">
              <input
                type="date"
                name="registration_start"
                value={formik.values.registration_start}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="h-[2.75rem] w-full rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] text-[0.938rem] text-[#111827] outline-none focus:border-[#0d9488]"
              />
            </Field>
            <Field label="Приём заявок до">
              <input
                type="date"
                name="registration_end"
                value={formik.values.registration_end}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="h-[2.75rem] w-full rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] text-[0.938rem] text-[#111827] outline-none focus:border-[#0d9488]"
              />
            </Field>
          </div>

          <Field label="Статус">
            <div className="flex flex-wrap gap-[0.5rem]">
              {STATUS_OPTIONS.map((opt) => {
                const active = formik.values.status === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => formik.setFieldValue('status', opt.value)}
                    className={`h-[2.5rem] rounded-[10px] border border-solid px-[1rem] text-[0.875rem] font-medium transition-colors ${
                      active
                        ? 'border-[#111827] bg-[#111827] text-white'
                        : 'border-[#e5e7eb] bg-white text-[#374151] hover:bg-[#f9fafb]'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </Field>

          <Field label="ФИО члена комиссии">
            <div className="flex flex-col sm:flex-row sm:items-center gap-[0.625rem] sm:gap-[1rem]">
              <input
                type="text"
                value={commissionInput}
                onChange={(e) => setCommissionInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCommissionMember()
                  }
                }}
                placeholder="Иванов Иван Иванович"
                className="h-[2.75rem] w-full sm:flex-1 rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1rem] text-[0.938rem] text-[#111827] placeholder:text-[#9ca3af] outline-none focus:border-[#0d9488]"
              />
              <button
                type="button"
                onClick={addCommissionMember}
                className="whitespace-nowrap text-[0.938rem] font-medium text-[#0d9488] hover:text-[#0b8278] self-start sm:self-auto"
              >
                + Добавить в комиссию
              </button>
            </div>

            {formik.values.commission.length > 0 && (
              <div className="mt-[0.75rem] flex flex-col gap-[0.5rem]">
                {formik.values.commission.map((member, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-[0.75rem] rounded-[10px] border border-solid border-[#e5e7eb] bg-[#f9fafb] px-[1rem] py-[0.625rem]"
                  >
                    <UserIcon />
                    <span className="flex-1 text-[0.938rem] text-[#111827]">{member.name_snapshot}</span>
                    <button
                      type="button"
                      onClick={() => removeCommissionMember(i)}
                      className="flex h-[1.5rem] w-[1.5rem] items-center justify-center rounded-[6px] hover:bg-[#f3f4f6]"
                    >
                      <XIcon size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-[0.625rem] border-t border-solid border-[#e5e7eb] px-[1.25rem] sm:px-[2rem] py-[1rem] sm:py-[1.25rem]">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border border-solid border-[#e5e7eb] bg-white px-[1.5rem] py-[0.75rem] text-[0.938rem] font-medium text-[#374151] hover:bg-[#f9fafb]"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="rounded-[10px] bg-[#0d9488] px-[1.5rem] py-[0.75rem] text-[0.938rem] font-medium text-white hover:bg-[#0b8278] disabled:opacity-60"
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddEditModal
